import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {PromptLibraryApi} from '../../../api/promptLibraryApi';
import {FaIcon} from '../../FaIcon';
import {Switch} from '../../buttons/Swtich';
import {YesNoModal} from '../../elements/YesNoModal';

export function PromptLibraryCard({prompt, clientId, onRated, onPromptUpdated, onForked, onForkConflict, onEdit, onDeactivated, highlighted, anchor = false}) {
    const {t} = useTranslation();
    const [copied, setCopied] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [forking, setForking] = useState(false);
    const [confirmDeactivate, setConfirmDeactivate] = useState(false);
    const [deactivating, setDeactivating] = useState(false);
    const social = prompt.social || {uses: 0, thumbs_up: 0, thumbs_down: 0, my_rating: null};
    const lock = prompt.lock || {enabled: true, missing: [], nudge_kind: null};
    const source = prompt.source || null;

    // El toggle de portal sólo existe para prompts de la agencia o del cliente que el usuario
    // puede editar: los GLOBAL son de Dashbo y no se publican al portal.
    const canTogglePortal = Boolean(onPromptUpdated && prompt.is_editable
        && (prompt.scope === 'AGENCY' || prompt.scope === 'CLIENT'));
    // «Duplicar y adaptar»: sólo con un cliente en contexto, y sobre prompts que todavía no son
    // de ese cliente (un prompt CLIENT ya es la versión adaptada).
    const canFork = Boolean(onForked && clientId && prompt.scope !== 'CLIENT');
    // Los DOS permisos los decide el backend y NO son el mismo: `is_editable` lo tiene cualquier
    // usuario de la agencia dueña, `is_deletable` SÓLO quien creó el prompt. Los prompts oficiales
    // de Dashbo (scope GLOBAL) vienen con los dos en false.
    const canEdit = Boolean(onEdit && prompt.is_editable);
    const canDeactivate = Boolean(onDeactivated && prompt.is_deletable);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(prompt.prompt_text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            // Ping de uso (fire-and-forget): no bloquea la copia.
            new APIClient(`${API_URL}agent/prompt-library/${prompt.id}/used/`)
                .call('POST', {agency_client_id: clientId});
        }).catch(() => toast.error(t('prompt_library.copy_error')));
    }, [prompt.prompt_text, prompt.id, clientId, t]);

    const rate = useCallback((clicked) => {
        const current = social.my_rating;
        const next = current === clicked ? null : clicked;
        const newSocial = {
            uses: social.uses,
            thumbs_up: social.thumbs_up - (current === 'UP' ? 1 : 0) + (next === 'UP' ? 1 : 0),
            thumbs_down: social.thumbs_down - (current === 'DOWN' ? 1 : 0) + (next === 'DOWN' ? 1 : 0),
            my_rating: next,
        };
        onRated(prompt.id, newSocial); // optimista
        new APIClient(`${API_URL}agent/prompt-library/${prompt.id}/rate/`)
            .call('POST', {value: next})
            .then((data) => {
                if (!data || data.status === 'ERROR' || data.status === 'NETWORK_ERROR') {
                    onRated(prompt.id, social); // revertir
                    toast.error(t('prompt_library.rate_error'));
                }
            });
    }, [social, prompt.id, onRated, t]);

    // Update optimista con revert: el switch responde al instante y sólo rebota si el backend
    // rechazó el cambio.
    const handlePortalToggle = useCallback((checked) => {
        const previous = Boolean(prompt.portal_visible);
        onPromptUpdated(prompt.id, {portal_visible: checked});
        PromptLibraryApi.setPortalVisibility(prompt.id, checked).then((data) => {
            if (!data || data.status === 'ERROR' || data.status === 'NETWORK_ERROR') {
                onPromptUpdated(prompt.id, {portal_visible: previous});
                toast.error(t('prompt_library.portal_toggle_error'));
            }
        });
    }, [prompt.id, prompt.portal_visible, onPromptUpdated, t]);

    const handleFork = useCallback(() => {
        if (forking) return;
        setForking(true);
        new APIClient(`${API_URL}agent/prompt-library/${prompt.id}/fork/`)
            .call('POST', {agency_client_id: clientId})
            .then((data) => {
                setForking(false);
                if (data && data.id) {
                    onForked(data);
                    return;
                }
                // 409 = ya existe una versión adaptada para este cliente; el backend manda su id
                // para poder mostrarla. `http_status`/`raw_body` los expone APIClient.
                if (data && data.http_status === 409) {
                    toast.warning(t('prompt_library.fork_exists'));
                    if (onForkConflict && data.raw_body && data.raw_body.fork_id) {
                        onForkConflict(data.raw_body.fork_id);
                    }
                    return;
                }
                toast.error(t('prompt_library.fork_error'));
            });
    }, [forking, prompt.id, clientId, onForked, onForkConflict, t]);

    // La baja es un soft-delete SIN vuelta desde la UI (el backend no expone restaurar), así que
    // va detrás de una confirmación in-app que lo dice con todas las letras.
    const handleDeactivate = useCallback(() => {
        if (deactivating) return;
        setDeactivating(true);
        PromptLibraryApi.deactivate(prompt.id).then((data) => {
            setDeactivating(false);
            if (!data || data.status === 'ERROR' || data.status === 'NETWORK_ERROR') {
                // El modal queda abierto a propósito: se puede reintentar o cancelar, y la card
                // sigue en la lista porque el prompt sigue activo.
                toast.error(t('prompt_library.deactivate_error'));
                return;
            }
            setConfirmDeactivate(false);
            onDeactivated(prompt.id);
        }).catch(() => {
            setDeactivating(false);
            toast.error(t('prompt_library.deactivate_error'));
        });
    }, [deactivating, prompt.id, onDeactivated, t]);

    const missingLabels = (lock.missing || []).map((ing) => t(`prompt_library.ingredients.${ing}`, ing)).join(', ');
    // `configure` (hoy sólo el margen de la Economía del cliente) no se desbloquea CONECTANDO nada:
    // se carga adentro de Dashbo, así que «Conecta Márgenes» decía el verbo equivocado (R-ecofe-23).
    // Sin link a propósito: esta card vive en las pantallas clásicas, y la carga de la economía es
    // de la 2.1 (con `ficha21` la pestaña IA redirige al Booster, donde el chip del margen sí lleva).
    const lockText = () => {
        if (lock.nudge_kind === 'plan') return t('prompt_library.lock_plan');
        if (lock.nudge_kind === 'configure') return t('client_panel.ia.unlock.margen_missing');
        return t('prompt_library.lock_connect', {ingredients: missingLabels, interpolation: {escapeValue: false}});
    };

    return (
        // `anchor` sólo en el render principal: el bloque starter repite prompts del grid y
        // dos elementos con el mismo id romperían el scroll del resaltado.
        <article id={anchor ? `pl-card-${prompt.id}` : undefined}
                 className={`pl-card ${lock.enabled ? '' : 'is-locked'} ${highlighted ? 'is-highlighted' : ''}`}>
            <div className="pl-card-chips">
                <span className={`pl-scope pl-scope-${prompt.scope}`}>{t(`prompt_library.scope.${prompt.scope}`)}</span>
                {source && (
                    <span className="info-icon-container pl-custom-badge">
                        <span className="pl-chip -custom">
                            <FaIcon iconName="pencil-alt"/> {t('prompt_library.badge_custom')}
                        </span>
                        <div className="warning-tooltip -below">
                            <div className="warning-tooltip-body">
                                <p className="warning-tooltip-text">
                                    {t('prompt_library.badge_custom_based_on',
                                        {title: source.title, interpolation: {escapeValue: false}})}
                                </p>
                            </div>
                        </div>
                    </span>
                )}
                {source && source.base_updated && (
                    <span className="pl-chip -hint">{t('prompt_library.badge_base_updated')}</span>
                )}
                {prompt.role && <span className="pl-chip">{t(`prompt_library.roles.${prompt.role}`)}</span>}
                {(prompt.momentos || []).map((m) => (
                    <span key={m} className="pl-chip">{t(`prompt_library.momentos.${m}`)}</span>
                ))}
                {(prompt.tags || []).map((tag) => (
                    <span key={tag} className="pl-chip -tag">{t(`prompt_library.tags.${tag}`, tag)}</span>
                ))}
            </div>

            <h4 className="pl-card-title">{prompt.title}</h4>
            {prompt.value_preview && <p className="pl-card-value">{prompt.value_preview}</p>}

            {lock.enabled ? (
                <>
                    {expanded && <pre className="prompt-text pl-card-prompt">{prompt.prompt_text}</pre>}
                    <div className="pl-card-actions">
                        <button type="button" className="btn -alt -negative pl-toggle" onClick={() => setExpanded((v) => !v)}>
                            <FaIcon iconName={expanded ? 'chevron-up' : 'chevron-down'}/> {t(expanded ? 'prompt_library.hide_prompt' : 'prompt_library.see_prompt')}
                        </button>
                        <button type="button" className={`btn -alt pl-copy ${copied ? 'is-copied' : ''}`} onClick={handleCopy}>
                            <FaIcon iconName={copied ? 'check' : 'copy'}/> {copied ? t('prompt_library.copied') : t('prompt_library.copy')}
                        </button>
                        <div className="pl-social">
                            <button
                                type="button"
                                className={`pl-thumb ${social.my_rating === 'UP' ? 'is-on' : ''}`}
                                onClick={() => rate('UP')}
                                aria-label={t('prompt_library.helpful')}
                                title={t('prompt_library.helpful')}
                            >
                                <FaIcon iconName="thumbs-up"/>{social.thumbs_up > 0 && <span>{social.thumbs_up}</span>}
                            </button>
                            <button
                                type="button"
                                className={`pl-thumb ${social.my_rating === 'DOWN' ? 'is-on -down' : ''}`}
                                onClick={() => rate('DOWN')}
                                aria-label={t('prompt_library.not_helpful')}
                                title={t('prompt_library.not_helpful')}
                            >
                                <FaIcon iconName="thumbs-down"/>{social.thumbs_down > 0 && <span>{social.thumbs_down}</span>}
                            </button>
                            {social.uses > 0 && (
                                <span className="pl-uses">{t('prompt_library.uses', {count: social.uses})}</span>
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <div className="pl-locked-nudge">
                    <FaIcon iconName="lock"/>
                    <span>{lockText()}</span>
                </div>
            )}

            {(canTogglePortal || canFork || canEdit || canDeactivate) && (
                <div className="pl-card-foot">
                    {canTogglePortal && (
                        // div, no <label>: el Switch ya renderiza su propio <label> y anidarlos
                        // es HTML inválido (y duplica la activación del checkbox).
                        <div className="pl-portal-toggle">
                            <span className="pl-portal-toggle-text">{t('prompt_library.portal_toggle')}</span>
                            <Switch
                                name={`pl-portal-${prompt.id}`}
                                checked={Boolean(prompt.portal_visible)}
                                onChange={(e) => handlePortalToggle(e.target.checked)}
                                style={{margin: 0}}
                            />
                        </div>
                    )}
                    {(canEdit || canDeactivate || canFork) && (
                        // Los `aria-label` llevan el título: en un grid de 50 cards, "Editar" a
                        // secas se repite 50 veces y no dice cuál.
                        <div className="pl-card-manage">
                            {canEdit && (
                                <button
                                    type="button"
                                    className="pl-manage-action"
                                    onClick={() => onEdit(prompt)}
                                    aria-label={t('prompt_library.edit_action_aria',
                                        {title: prompt.title, interpolation: {escapeValue: false}})}
                                >
                                    <FaIcon iconName="pencil-alt"/>
                                    {t('prompt_library.edit_action')}
                                </button>
                            )}
                            {canDeactivate && (
                                <button
                                    type="button"
                                    className="pl-manage-action -danger"
                                    onClick={() => setConfirmDeactivate(true)}
                                    disabled={deactivating}
                                    aria-label={t('prompt_library.deactivate_action_aria',
                                        {title: prompt.title, interpolation: {escapeValue: false}})}
                                >
                                    <FaIcon iconName="ban"/>
                                    {t('prompt_library.deactivate_action')}
                                </button>
                            )}
                            {canFork && (
                                <button type="button" className="pl-fork" onClick={handleFork} disabled={forking}>
                                    <FaIcon iconName="copy"/>
                                    {t(forking ? 'prompt_library.forking' : 'prompt_library.fork_action')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {canDeactivate && (
                <YesNoModal
                    skipRenderButton
                    isOpen={confirmDeactivate}
                    title={t('prompt_library.deactivate_title')}
                    message={t('prompt_library.deactivate_message',
                        {title: prompt.title, interpolation: {escapeValue: false}})}
                    question={t('prompt_library.deactivate_question')}
                    isSaving={deactivating}
                    onYesAnswer={handleDeactivate}
                    onNoAnswer={() => setConfirmDeactivate(false)}
                />
            )}
        </article>
    );
}
